import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { formatDateTime12 } from "@/utils/helpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Eye, Plus, Ticket } from "lucide-react";
import type { PatientV2 } from "../types";
import { patientV2CardBadgeClass, patientV2SoftBadgeClass } from "../constants";
import TableRowCardWithSearch from "./TableRowCardWithSearch";
import { PatientV2ExpandableRowCard } from "./PatientV2ExpandableRowCard";
import { PatientV2CardSubtitle } from "./PatientV2CardSubtitle";
import { PatientV2ExpandableCardActions } from "./PatientV2ExpandableCardActions";
import { SupportTicketCreateDialog } from "../support/SupportTicketCreateDialog";
import { SupportTicketViewDialog } from "../support/SupportTicketViewDialog";
import {
  formatSupportTicketType,
  resolveSupportCreatedByName,
  resolveSupportPatientName,
} from "../support/supportTicketConstants";

const EMPTY_FILTERS = {};

const SUPPORT_COLUMNS = [
  { id: "ticketId", label: "Ticket ID", isSortable: false },
  { id: "ticketType", label: "Ticket Type", isSortable: true },
  { id: "patientName", label: "Patient Name", isSortable: false },
  { id: "createdByName", label: "Created By", isSortable: false },
  { id: "createdAt", label: "Created At", isSortable: true },
  { id: "lastUpdatedImpilo", label: "Last Updated", isSortable: true },
  { id: "status", label: "Status", isSortable: false },
] as const;

function resolvePatientDisplayName(patient: PatientV2 | null | undefined): string {
  if (!patient) return "";
  return [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" ").trim();
}

export function SupportTab({
  patientId,
  patient,
}: {
  patientId: string;
  patient?: PatientV2 | null;
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState<Record<string, unknown> | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const patientDisplayName = useMemo(() => resolvePatientDisplayName(patient), [patient]);

  const viewData = useMemo(
    () =>
      (rows || []).map((ticket) => ({
        ...ticket,
        ticketType: formatSupportTicketType(ticket?.ticketType),
        patientName: resolveSupportPatientName(ticket),
        createdByName: resolveSupportCreatedByName(ticket),
        createdAt: ticket?.createdAtImpilo
          ? formatDateTime12(String(ticket.createdAtImpilo))
          : "—",
        lastUpdatedImpilo: ticket?.lastUpdatedImpilo
          ? formatDateTime12(String(ticket.lastUpdatedImpilo))
          : "—",
        status: ticket?.open ? "Open" : "Closed",
      })),
    [rows],
  );

  const openTicketDetails = (ticket: Record<string, unknown>) => {
    setSelectedTicket(ticket);
    setViewOpen(true);
  };

  const listParams = useMemo(
    () => ({
      patientId: patientId || undefined,
      refreshKey,
    }),
    [patientId, refreshKey],
  );

  return (
    <div className="teachers">
      <div className="mb-3 flex flex-col gap-3 px-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="teachers flex items-center gap-2 text-xl font-bold">Support Tickets</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="mr-1 h-4 w-4" />
          New Ticket
        </Button>
      </div>

      <TableRowCardWithSearch
        heading="Support Tickets"
        url="/impilo/support-tickets"
        params={listParams}
        apiDataKey="content"
        fetchCondition={true}
        isIncludeSearch={false}
        isIgnoreSearchParams
        searchPlaceholder=""
        columnHeaders={SUPPORT_COLUMNS as never}
        data={rows as never[]}
        viewData={viewData}
        setData={setRows}
        isActionColumn={false}
        handleAction={() => {}}
        filtersOptions={[]}
        filters={EMPTY_FILTERS}
        setFilters={() => {}}
        clickRow={() => {}}
        moreActions={[
          {
            label: "View Ticket",
            icon: <Eye className="h-4 w-4" />,
            isShow: () => true,
            onClick: (ticket: Record<string, unknown>) => openTicketDetails(ticket),
          },
          {
            label: "Open Ticket Page",
            icon: <ExternalLink className="h-4 w-4" />,
            isShow: () => true,
            onClick: (ticket: Record<string, unknown>) => {
              const ticketRouteId = ticket?._id || ticket?.ticketId || "";
              if (!ticketRouteId) return;
              navigate(`/support/${String(ticketRouteId)}`);
            },
          },
        ]}
        key={`support-cards-${patientId}-${refreshKey}`}
        renderRowCard={({ viewRow, row, actions }) => {
          const v = viewRow as Record<string, unknown>;
          const ticketType = String(v?.ticketType || "—");
          const ticketId = String(v?.ticketId || "—");
          const createdByName = String(v?.createdByName || "—");
          const status = String(v?.status || "—");

          return (
            <PatientV2ExpandableRowCard
              icon={<Ticket className="h-5 w-5 text-muted-foreground" />}
              title={ticketId}
              subtitle={
                <PatientV2CardSubtitle
                  parts={[{ label: "Created By", value: createdByName }]}
                />
              }
              headerBadges={
                <>
                  {ticketType !== "—" ? (
                    <Badge className={patientV2SoftBadgeClass}>{ticketType}</Badge>
                  ) : null}
                  {status !== "—" ? (
                    <Badge variant="outline" className={patientV2CardBadgeClass}>
                      {status}
                    </Badge>
                  ) : null}
                </>
              }
              details={[
                { label: "Ticket Type", value: ticketType },
                { label: "Patient Name", value: String(v?.patientName || "—") },
                { label: "Created By", value: createdByName },
                { label: "Created At", value: String(v?.createdAt || "—") },
                { label: "Last Updated", value: String(v?.lastUpdatedImpilo || "—") },
              ]}
              actions={<PatientV2ExpandableCardActions actions={actions} row={row} />}
            />
          );
        }}
      />

      <SupportTicketViewDialog
        open={viewOpen}
        onOpenChange={(open) => {
          setViewOpen(open);
          if (!open) setSelectedTicket(null);
        }}
        ticket={selectedTicket}
      />

      <SupportTicketCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        patientId={patientId}
        patientDisplayName={patientDisplayName}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
